<template>
    <div
        class="license-plan"
        :class="[`license-plan--${type}`, { 'license-plan--selected': isSelected, disabled: isDisabled }]"
        @click="selectPlan"
    >
        <div class="left-section">
            <div class="left-section-header">
                <PrimeRadioButton @click="selectPlan" v-model="selectedPlanType" :name="name" :value="type" :disabled="isDisabled" />
                <div class="license-title-container">
                    <div class="license-name-price-container">
                        <div class="flex items-center">
                            <span>{{ name }}</span>
                            <span v-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0" class="available-licenses-badge">
                                {{ availableLicenses }}
                            </span>
                        </div>
                    </div>
                    <span v-if="license?.slug === type && purchaseType === PurchaseType.UPGRADE" class="current-license-badge">Current License</span>
                </div>
                <span v-if="isDisabled" class="text-zinc-500 text-sm">(Not available monthly)</span>
            </div>
            <p>{{ description }}</p>
            <div v-if="upgradeNotice && isSelected && purchaseType === PurchaseType.UPGRADE" class="notice-message">
                <ExclamationTriangleIcon class="warning-icon" />
                <span>{{ upgradeNotice }}</span>
            </div>
        </div>
        <div class="right-section">
            <span v-if="store.isLoadingLicenseCosts" class="loading-price">
                <LoadingDots />
            </span>
            <span v-else-if="purchaseType === PurchaseType.GIFT && availableLicenses > 0">
                {{ availableLicenses }} Available
            </span>
            <div v-else class="price-div">
                <span class="price-text">
                    {{ priceParts.dollars }}<span v-if="priceParts.cents !== null"><sup class="cents-sup">{{ priceParts.cents }}</sup></span>
                </span>
                <span class="user-time-text">User / {{ LicenseRenewalTermDisplay[selectedTerm] }}</span>
            </div>
        </div>
    </div>

    <div class="benefits" :class="`benefits--${type}`" v-if="isSelected">
        <div class="benefits-list">
            <div v-for="(benefit, index) in store.benefits[type]" :key="index" class="benefit">
                <CheckCircleIcon class="check-icon" />
                <p>{{ benefit }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import PrimeRadioButton from 'primevue/radiobutton';
import LoadingDots from '~/components/main/shared/LoadingDots.vue';
import useUtils from '~/composables/useUtils';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { LicenseRenewalTerm, LicenseRenewalTermDisplay, LicenseType, LicenseTypeName, PurchaseType } from '~/types/licenses/licenses';

const props = defineProps<{
    type: LicenseType;
    description: string;
    unavailableMonthly?: boolean;
    upgradeNotice?: string;
}>();

const store = useSubscribeStore();
const { license, selectedPlanType, purchaseType, selectedTerm } = storeToRefs(store);
const { formatCurrency } = useUtils();
const personStore = usePersonStore();

const name = computed(() => LicenseTypeName[props.type]);
const isSelected = computed(() => selectedPlanType.value === props.type);
const isDisabled = computed(() => props.unavailableMonthly === true && selectedTerm.value === LicenseRenewalTerm.MONTHLY);

const availableLicenses = computed(() => {
    if (purchaseType.value !== PurchaseType.GIFT) return 0;

    const matchingLicenses = personStore.person?.licenses.filter(license => {
        return license.slug === props.type &&
               license.term === selectedTerm.value &&
               license.expired === false &&
               !license.assigned_to;
    });

    return matchingLicenses?.length || 0;
});

const priceParts = computed(() => {
    const formatted = formatCurrency(store.getCostPerLicenseAfterAnnualDiscount(props.type));
    const match = formatted.match(/^(.*?)[.,](\d{2})$/);
    if (match) {
        return { dollars: match[1], cents: match[2] };
    }
    return { dollars: formatted, cents: null };
});

const selectPlan = () => {
    if (!isDisabled.value) {
        selectedPlanType.value = props.type;
    }
};
</script>

<style lang="scss" scoped>
.license-plan {
    @apply flex justify-between items-start px-8 py-6;
    @apply border border-zinc-300 cursor-pointer transition-all duration-300 ease-in-out;
    @apply flex-col lg:flex-row gap-4 lg:gap-0;

    &:hover {
        @apply -translate-y-[2px] bg-blue-10;
    }

    .left-section {
        @apply flex-col gap-1;

        p {
            @apply text-zinc-600 text-sm font-normal mt-1 leading-6;
        }

        .left-section-header {
            @apply flex items-center gap-3;

            .license-title-container {
                @apply flex gap-2;

                .license-name-price-container {
                    @apply flex items-center justify-between w-full;

                    span:first-child {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8;
                    }

                    .price-text {
                        @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
                    }

                    .user-time-text {
                        @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
                    }
                }

                .current-license-badge {
                    @apply bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }

                .available-licenses-badge {
                    @apply bg-violet-500 text-white text-xs font-medium ml-2 px-2 py-1 rounded-full inline-flex items-center justify-center min-w-0 w-auto flex-shrink-0;
                }
            }
        }

        .notice-message {
            @apply inline-flex items-center gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md;
            width: fit-content;

            .warning-icon {
                @apply w-4 h-4 text-amber-500 flex-shrink-0;
            }

            span {
                @apply text-amber-700 text-sm font-medium whitespace-nowrap;
            }
        }
    }

    .right-section {
        span {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .price-div {
            @apply flex flex-row lg:flex-col items-center lg:items-end text-right gap-3 lg:gap-0;
        }

        .price-text {
            @apply text-zinc-900 font-inter text-2xl font-semibold leading-8 text-end;
        }

        .user-time-text {
            @apply text-zinc-600 font-inter text-sm font-normal leading-6 text-end;
        }
    }
}

.license-plan--essentials {
    @apply rounded-t-lg border-b-0;

    .right-section {
        @apply flex justify-end items-start;
    }
}

.license-plan--pro {
    @apply border-b-0;

    .right-section {
        @apply flex justify-end items-start;

        span {
            @apply whitespace-nowrap;
        }
    }
}

.license-plan--signature {
    @apply rounded-b-lg;

    &.license-plan--selected {
        @apply rounded-b-none border-b-0;
    }

    .right-section {
        @apply grid justify-items-start lg:justify-items-end sm:w-52 w-full;
    }
}

.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
        background: inherit;
        transform: none;
    }
}

.benefits {
    @apply flex flex-col items-start gap-6 self-stretch p-8 lg:p-6 border border-zinc-300;
}

.benefits--pro {
    @apply border-b-0;
}

.benefits--signature {
    @apply rounded-b-lg;
}

.benefits-list {
    @apply grid grid-cols-1 md:grid-cols-2 gap-4 self-stretch;
}

.benefit {
    @apply flex items-start gap-3;

    p {
        @apply text-zinc-600 font-inter text-sm font-normal leading-6 w-full;
    }
}

.check-icon {
    @apply w-6 h-6 text-green-500;
}

.cents-sup {
    @apply text-xs align-super leading-none relative -top-1;
}
</style>
